import React from 'react'
import { useIntl } from 'react-intl'
import { useLanguage } from '../../contexts/LanguageContext'
import { useAuth } from '../../contexts/AuthContext'
import { languageName, PSEUDO_LOCALE } from '../../i18n/config'
import { authApi } from '../../utils/api'

/**
 * Compact language selector for the app shell. Lists the enabled locales (plus
 * the pseudo-locale in dev) from the shared registry, switches the active locale
 * immediately, and — when the user is signed in — persists the choice to their
 * profile (PATCH /users/me) so it follows them across devices.
 */
export const LanguagePicker: React.FC<{ className?: string }> = ({ className }) => {
  const intl = useIntl()
  const { locale, setLocale, availableLocales } = useLanguage()
  const { user } = useAuth()

  // A single enabled locale — nothing to pick yet.
  if (availableLocales.length < 2) return null

  const onChange = (next: string) => {
    setLocale(next)
    // Persist real locales only; the dev pseudo-locale is never stored server-side.
    if (user && next !== PSEUDO_LOCALE) {
      authApi.updateLocale(next).catch((err) => {
        console.error('Failed to persist locale preference:', err)
      })
    }
  }

  return (
    <label className={`relative inline-block ${className || ''}`}>
      <span className="sr-only">{intl.formatMessage({ id: 'common.language' })}</span>
      {/* The native select chrome puts its arrow hard against the rounded edge,
          so the control draws its own and keeps room for it. Sizes are explicit
          pixels on purpose: this project remaps Tailwind's spacing scale (4 is
          32px here, not 16px), so a w-4 icon would be twice the size meant and
          would sit on top of the label. */}
      <select
        aria-label={intl.formatMessage({ id: 'common.language' })}
        value={locale}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none text-sm bg-surface border border-border rounded-lg pl-[12px] pr-[30px] py-[6px] text-foreground-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-input-focus transition-colors cursor-pointer"
      >
        {availableLocales.map((loc) => (
          <option key={loc} value={loc}>
            {languageName(loc)}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 h-[14px] w-[14px] text-foreground-muted"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </label>
  )
}
